/** HTTP projection only. The server owns admission, scope and calculations. */
export interface ReportVisit {
    id: number;
    date: string;
    doctorId: number | null;
    status: string;
    cohort: 'new' | 'returning' | null;
    caseSheetStatus: string | null;
    conversionState: string | null;
}
export interface ReportMetric {
    key: 'walkins' | 'converted' | 'nonConverted' | 'conversion' | 'collections' | 'ticket';
    label: string;
    value: number | null;
    unit: 'visits' | 'percent' | 'minor_units';
    reason: string | null;
}
export interface ReportPeriod {
    from: string;
    to: string;
    visits: readonly ReportVisit[] | null;
    collectionsMinorUnits: number | null;
    collectionReason: string | null;
    newWalkins: number | null;
    doctorClosed: number | null;
    metrics: readonly ReportMetric[];
}
export interface Receipt {
    readonly methodDescription?: string;
    id: number;
    date: string;
    amountMinorUnits: number;
    appointmentId: number | null;
}
export interface ClinicReport {
    role: 'doctor' | 'receptionist';
    clinicId: number;
    doctorId: number | null;
    doctors: readonly {
        id: number;
        name: string;
    }[];
    selected: ReportPeriod;
    comparison: ReportPeriod | null;
    receipts: {
        selected: readonly Receipt[] | null;
        comparison: readonly Receipt[] | null;
    };
    canExport: boolean;
    asOf: string;
    timezone: 'Asia/Kolkata';
    products: {selected: ReportProductRow[]; comparison: ReportProductRow[] | null;selectedTotal:number|null;comparisonTotal:number|null};
    productReason: string;
    daily: {
        selected: readonly ReportDailyPoint[];
        comparison: readonly ReportDailyPoint[] | null;
    };
    cash: {
        selected: ClinicCash | null;
        comparison: ClinicCash | null;
        reason: string | null;
    };
    sourceSplit: {
        status: 'unavailable';
        reason: string;
    };
}
export function metricValue(value: number | null, unit: ReportMetric['unit']): string {
    if (value === null)
        return '—';
    if (unit === 'minor_units')
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value / 100);
    return `${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 1 }).format(value)}${unit === 'percent' ? '%' : ''}`;
}
export function changeLabel(current: ReportMetric, previous: ReportMetric | undefined): string {
    if (!previous || current.value === null || previous.value === null)
        return 'Comparison unavailable';
    if (current.unit === 'percent') {
        const n = current.value - previous.value;
        return `${n > 0 ? '+' : ''}${n.toFixed(1)} pp`;
    }
    if (previous.value === 0)
        return 'Comparison base is zero';
    const n = (current.value - previous.value) / previous.value * 100;
    return `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
}
export type EvidenceMetric = ReportMetric['key'] | 'closed';
export interface ReportDailyPoint {
    date: string;
    metrics: readonly ReportMetric[];
}
export interface CashMovement {
    key:string; businessDate:string; atIso:string|null; typeToken:'day_start'|'collection'|'expense'|'petty_cash'|'deposit'|'reversal'|'day_close';
    amountMinorUnits:number; signedMinorUnits:number; runningCashMinorUnits:number|null; entryId:number|null; paymentId:number|null;
}
export interface CashDay {
    businessDate: string;
    openingMinorUnits: number | null;
    closingMinorUnits: number | null;
    derivedCloseMinorUnits: number | null;
    countedCloseMinorUnits: number | null;
    differenceMinorUnits: number | null;
    acknowledged: boolean;
    cashInMinorUnits: number;
    spentMinorUnits: number;
    sentMinorUnits: number;
    postCloseStatus?: string;
    postCloseAdjustmentMinorUnits?: number | null;
    postCloseCorrected?: boolean;
}
export interface ClinicCash {
    summary: {
        openingMinorUnits: number | null;
        closingMinorUnits: number | null;
        expensesMinorUnits: number;
        depositsMinorUnits: number;
        collectionsCapped: boolean;
        entriesCapped: boolean;
        countedAdjustmentMinorUnits: number;
    };
    days: readonly CashDay[];
    movements: readonly CashMovement[] | null;
    chain: {
        state: string;
        pendingDates: readonly string[];
        carriedFromDate: string | null;
        source: string | null;
    };
    postCloseAdjustmentMinorUnits: number | null;
    postCloseAdjustmentStatus: string | null;
}
export function evidenceRows(report: ClinicReport, period: 'selected' | 'comparison', metric: EvidenceMetric, kind: 'visits' | 'payments'): readonly (ReportVisit | Receipt)[] | null {
    const p = report[period];
    if (!p)
        return null;
    if (kind === 'payments')
        return metric === 'collections' || metric === 'ticket' ? report.receipts[period] : null;
    const visits = p.visits;
    if (visits === null || metric === 'collections')
        return null;
    if (metric === 'closed')
        return p.doctorClosed === null ? null : visits.filter(v => v.caseSheetStatus === 'finalized' && ['fulfilled', 'completed'].includes(v.status));
    if (metric === 'ticket')
        return p.newWalkins === null ? null : visits.filter(v => v.cohort === 'new');
    if (['converted', 'nonConverted', 'conversion'].includes(metric) && visits.some(v => v.conversionState === null))
        return null;
    const converted = (v: ReportVisit) => ['converted_first_payment', 'converted_full_package'].includes(v.conversionState ?? '');
    return metric === 'converted' ? visits.filter(converted) : metric === 'nonConverted' ? visits.filter(v => !converted(v)) : visits;
}
export type CashMetric='cashOpening'|'cashReceipts'|'cashDeposits'|'cashExpenses'|'cashClosing';
export const cashMetrics:Record<CashMetric,{label:string;field:'openingMinorUnits'|'cashInMinorUnits'|'sentMinorUnits'|'spentMinorUnits'|'derivedCloseMinorUnits'}>={cashOpening:{label:'Opening balance',field:'openingMinorUnits'},cashReceipts:{label:'Cash receipts',field:'cashInMinorUnits'},cashDeposits:{label:'Bank deposits',field:'sentMinorUnits'},cashExpenses:{label:'Cash expenses',field:'spentMinorUnits'},cashClosing:{label:'Expected closing',field:'derivedCloseMinorUnits'}};

export function cashEvidenceMovements(cash:ClinicCash,metric:CashMetric):readonly CashMovement[]|null {
    if(!cash.movements||cash.summary.collectionsCapped||cash.summary.entriesCapped)return null;
    return cash.movements.filter(r=>metric==='cashReceipts'?r.typeToken==='collection'&&r.signedMinorUnits>0:metric==='cashExpenses'?['expense','petty_cash','reversal'].includes(r.typeToken):metric==='cashDeposits'?['deposit','reversal'].includes(r.typeToken):false);
}

export interface ReportProductMetric {status:'complete'|'partial'|'unavailable'|'excluded';value:number|null;unit:string;reason:string|null}
export interface ReportProductRow {key:string;label:string;unitLabel:string;units:ReportProductMetric;revenue:ReportProductMetric}
